// src/pages/CorporateResourcesPage.jsx
import { useEffect, useState } from 'react';
import {
  ActionIcon,
  Badge,
  Button,
  Group,
  Paper,
  ScrollArea,
  SimpleGrid,
  Stack,
  Table,
  Tabs,
  Text,
  TextInput,
  ThemeIcon,
  Title,
  Box,
  Flex,
} from '@mantine/core';
import {
  IconBooks,
  IconMail,
  IconQuestionMark,
  IconSearch,
  IconCopy,
  IconExternalLink,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import api from '../api/axiosConfig';

function StatCard({ title, value, icon, color = 'blue' }) {
  return (
    <Paper
      withBorder
      radius="xl"
      p="lg"
      style={{
        background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
        boxShadow: '0 8px 24px rgba(15,23,42,0.05)',
      }}
    >
      <Group justify="space-between">
        <div>
          <Text c="dimmed" size="sm" fw={600}>{title}</Text>
          <Text fw={900} size="xl">{value}</Text>
        </div>
        <ThemeIcon size={52} radius="xl" variant="light" color={color}>
          {icon}
        </ThemeIcon>
      </Group>
    </Paper>
  );
}

export default function CorporateResourcesPage() {
  const [overview, setOverview] = useState(null);
  const [mailGroups, setMailGroups] = useState([]);
  const [signatures, setSignatures] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [faqs, setFaqs] = useState([]);

  const [search, setSearch] = useState('');
  const [signatureModalOpened, setSignatureModalOpened] = useState(false);
  const [selectedSignature, setSelectedSignature] = useState(null);

  const fetchAll = async () => {
    try {
      const [overviewRes, mailRes, signRes, docRes, faqRes] = await Promise.all([
        api.get('/corporate-resources/overview'),
        api.get('/corporate-resources/mail-groups', { params: { search } }),
        api.get('/corporate-resources/signatures'),
        api.get('/corporate-resources/documents', { params: { search } }),
        api.get('/corporate-resources/faqs'),
      ]);

      setOverview(overviewRes.data);
      setMailGroups(mailRes.data || []);
      setSignatures(signRes.data || []);
      setDocuments(docRes.data || []);
      setFaqs(faqRes.data || []);
    } catch (err) {
      console.error(err);
      notifications.show({
        title: 'Hata',
        message: 'Kurumsal kaynak verileri alınamadı.',
        color: 'red',
      });
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      fetchAll();
    }, 300);
    return () => clearTimeout(t);
  }, [search]);

  const copyText = async (text, label = 'İçerik') => {
    try {
      await navigator.clipboard.writeText(text || '');
      notifications.show({
        title: 'Başarılı',
        message: `${label} panoya kopyalandı.`,
        color: 'teal',
      });
    } catch {
      notifications.show({
        title: 'Hata',
        message: 'Kopyalama yapılamadı.',
        color: 'red',
      });
    }
  };

  return (
    <Stack gap="lg" style={{ minHeight: '70vh' }}>
      <Paper
        radius="xl"
        p={{ base: 'lg', md: 'xl' }}
        style={{
          position: 'relative',
          overflow: 'hidden',
          background:
            'linear-gradient(135deg, rgba(15,23,42,0.98) 0%, rgba(30,41,59,0.96) 40%, rgba(67,56,202,0.88) 100%)',
          border: '1px solid rgba(255,255,255,0.08)',
          boxShadow: '0 18px 45px rgba(15,23,42,0.18)',
        }}
      >
        <Box
          style={{
            position: 'absolute',
            top: -70,
            right: -70,
            width: 220,
            height: 220,
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.08)',
          }}
        />
        <Flex justify="space-between" align="center" wrap="wrap" gap="md" style={{ position: 'relative', zIndex: 1 }}>
          <Group align="flex-start" wrap="nowrap">
            <ThemeIcon size={54} radius="xl" variant="white" color="rgba(255,255,255,0.12)">
              <IconBooks size={28} color="black" />
            </ThemeIcon>
            <div>
              <Text fw={700} size="sm" c="rgba(255,255,255,0.75)">Kurumsal Bilgi ve Referans</Text>
              <Title order={2} c="white" fw={900}>Kurumsal Kaynak Merkezi</Title>
              <Text size="sm" maw={760} style={{ color: 'rgba(255,255,255,0.78)', lineHeight: 1.7 }}>
                Mail grupları, imza şablonları, prosedür dokümanları ve sık sorulan sorulara tek ekrandan erişin.
              </Text>
            </div>
          </Group>

          <TextInput
            placeholder="Ara..."
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
            leftSection={<IconSearch size={16} />}
            radius="xl"
            w={{ base: '100%', md: 320 }}
          />
        </Flex>
      </Paper>

      <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="lg">
        <StatCard title="Mail Grubu" value={overview?.total_mail_groups ?? 0} color="blue" icon={<IconMail size={24} />} />
        <StatCard title="Doküman" value={overview?.total_documents ?? 0} color="indigo" icon={<IconBooks size={24} />} />
        <StatCard title="SSS" value={overview?.total_faqs ?? 0} color="orange" icon={<IconQuestionMark size={24} />} />
      </SimpleGrid>

      <Paper withBorder radius="xl" p="md" style={{ background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)' }}>
        <Tabs defaultValue="mail-groups" color="indigo" radius="xl">
          <Tabs.List>
            <Tabs.Tab value="mail-groups">Mail Grupları</Tabs.Tab>
            <Tabs.Tab value="documents">Dokümanlar</Tabs.Tab>
            <Tabs.Tab value="faqs">SSS</Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="mail-groups" pt="md">
            <ScrollArea>
              <Table striped highlightOnHover verticalSpacing="md">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Grup Adı</Table.Th>
                    <Table.Th>E-posta</Table.Th>
                    <Table.Th>Departman</Table.Th>
                    <Table.Th>Erişim</Table.Th>
                    <Table.Th>Sorumlu</Table.Th>
                    <Table.Th>Açıklama</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {mailGroups.map((item) => (
                    <Table.Tr key={item.id}>
                      <Table.Td><Text fw={700}>{item.group_name}</Text></Table.Td>
                      <Table.Td>
                        <Group gap={6}>
                          <Text size="sm">{item.email}</Text>
                          <ActionIcon variant="light" radius="xl" onClick={() => copyText(item.email, 'Mail adresi')}>
                            <IconCopy size={15} />
                          </ActionIcon>
                        </Group>
                      </Table.Td>
                      <Table.Td>{item.department || '-'}</Table.Td>
                      <Table.Td><Badge variant="light" radius="xl">{item.access_type}</Badge></Table.Td>
                      <Table.Td>{item.owner_name || '-'}</Table.Td>
                      <Table.Td><Text size="sm">{item.description || '-'}</Text></Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </ScrollArea>
          </Tabs.Panel>

          <Tabs.Panel value="signatures" pt="md">
            <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
              {signatures.map((item) => (
                <Paper key={item.id} withBorder radius="xl" p="md">
                  <Group justify="space-between" mb="sm">
                    <div>
                      <Text fw={700}>{item.name}</Text>
                      <Text size="xs" c="dimmed">{item.language.toUpperCase()} • {item.employee_type}</Text>
                    </div>
                    {item.is_default && <Badge radius="xl" color="teal">Varsayılan</Badge>}
                  </Group>

                  <Paper withBorder radius="lg" p="sm" bg="gray.0" mb="sm">
                    <Text size="sm" lineClamp={4}>
                      {item.plain_text_content || 'İmza önizlemesi yok'}
                    </Text>
                  </Paper>

                  <Group justify="flex-end">
                    <Button
                      variant="light"
                      radius="xl"
                      onClick={() => {
                        setSelectedSignature(item);
                        setSignatureModalOpened(true);
                      }}
                    >
                      Görüntüle
                    </Button>
                  </Group>
                </Paper>
              ))}
            </SimpleGrid>
          </Tabs.Panel>

          <Tabs.Panel value="documents" pt="md">
            <ScrollArea>
              <Table striped highlightOnHover verticalSpacing="md">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Başlık</Table.Th>
                    <Table.Th>Kategori</Table.Th>
                    <Table.Th>Versiyon</Table.Th>
                    <Table.Th>Departman</Table.Th>
                    <Table.Th>Özet</Table.Th>
                    <Table.Th>Dosya</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {documents.map((item) => (
                    <Table.Tr key={item.id}>
                      <Table.Td><Text fw={700}>{item.title}</Text></Table.Td>
                      <Table.Td>{item.category?.name || '-'}</Table.Td>
                      <Table.Td><Badge variant="light" radius="xl">{item.version}</Badge></Table.Td>
                      <Table.Td>{item.department || '-'}</Table.Td>
                      <Table.Td><Text size="sm">{item.summary || '-'}</Text></Table.Td>
                      <Table.Td>
                        {item.file_url ? (
                          <Button
                            component="a"
                            href={item.file_url}
                            target="_blank"
                            variant="light"
                            radius="xl"
                            rightSection={<IconExternalLink size={15} />}
                          >
                            Aç
                          </Button>
                        ) : (
                          <Text size="sm" c="dimmed">Dosya yok</Text>
                        )}
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </ScrollArea>
          </Tabs.Panel>

          <Tabs.Panel value="faqs" pt="md">
            <Stack gap="md">
              {faqs.map((item) => (
                <Paper key={item.id} withBorder radius="xl" p="md">
                  <Text fw={700}>{item.question}</Text>
                  <Text size="sm" c="dimmed" mt={6}>{item.answer}</Text>
                </Paper>
              ))}
            </Stack>
          </Tabs.Panel>
        </Tabs>
      </Paper>
    </Stack>
  );
}